// This file will monkey patch setTimeout and setInterval functions in javascript,
//  wrapping all function invocations with a try catch which will funnel to our logger.
// Note that this behavior will only kick in once a logger has been registered, handled in the sibling file,
//  unhandled-error-registration.ts .
import { LoggingService, ErrorDetails } from "../../ServiceDesk/sdServices/loggingSvc";
import ErrorStackParser = require("error-stack-parser/error-stack-parser");

let localLoggingService: LoggingService;
let logQueue: ErrorDetails[] = [];
export let setLoggingService = (loggingService: LoggingService) => {
  localLoggingService = loggingService;
  logQueue.forEach((error) => {
    localLoggingService.logError(error);
  });
  logQueue.length = 0;
};

let logIfAble = (e: ErrorDetails, throwIfLoggerAbsent: boolean) => {
  e.unhandled = true;
  (<any>e)._sd_processed = true;
  if (!shouldLogAfterHandlingSpecialCases(e)) {
    return;
  }

  if (localLoggingService) {
    localLoggingService.logError(e);
  } else {
    logQueue.push(e);
    if (throwIfLoggerAbsent) {
      throw e;
    }
  }
};

class WrappedInvocationStats {
  private delayOcurrences = 0;
  private totalDelay = 0;
  private totalDelta = 0;
  private totalInvocations = 0;
  private delayedDelta = 0;
  private maxDelay = 0;

  public isDelayed(actualDelay: number, expectedDelay?: number): boolean {
    this.totalInvocations++;
    if (!expectedDelay || isNaN(expectedDelay) || expectedDelay <= 100) {
      expectedDelay = 100;
    }

    if (actualDelay > expectedDelay) {
      let delta = actualDelay - expectedDelay;
      this.totalDelta += delta;
      this.addDelay(actualDelay);
      // If delta is more than 50% off from expected, then log error.
      if (delta / expectedDelay > 0.5) {
        this.delayOcurrences++;
        this.delayedDelta += delta;
        return true;
      }
    }
    return false;
  }

  public getStatsObject(): any {
    return {
      averageDelay: this.totalDelay / this.totalInvocations,
      averageDelta: this.totalDelta / this.totalInvocations,
      averageDelayedDelta: this.delayedDelta / this.delayOcurrences,
      delayOcurrences: this.delayOcurrences,
      totalInvocations: this.totalInvocations,
      maxDelay: this.maxDelay
    };
  }

  private addDelay(delay: number) {
    if (delay > this.maxDelay) {
      this.maxDelay = delay;
    }
    this.totalDelay += delay;
  }
}

let debounceTracker: { [identifier: string]: boolean } = {};
let shouldLogDebouncedError = function (e: Error, identifier: string) {
  if (debounceTracker[identifier]) {
    return false;
  } else {
    debounceTracker[identifier] = true;
    return true;
  }
};

let shouldLogAfterHandlingSpecialCases = function (e: Error): boolean {
  if (typeof e == "object") {
    if (e.message) {
      let windowsOutOfMemory = "Not enough storage is available to complete this operation";
      if (e.message.includes(windowsOutOfMemory)) {
        return shouldLogDebouncedError(e, windowsOutOfMemory);
      }

      let digestInProgress = "$digest already in progress";
      if (e.message.includes(digestInProgress)) {
        if (e.stack) {
          let stackFrames = ErrorStackParser.parse(e);
          let files = [];
          for (let i = 0; i < stackFrames.length; i++) {
            if (stackFrames[i].fileName) {
              files.push(stackFrames[i].fileName);
            }
          }

          // Remove the current stack as it's going to be this file.
          files.pop();
          // Find any non lib frames
          let result = files.filter((x) => {
            return !x.match(".*sd-.{10}.lib-min.js");
          });

          // If any non lib frames exist, we still want to log because this could be a product issue.
          if (result && result.length > 0) {
            return true;
          } else {
            // No non-lib frames exist, likely a result of us tapping into too much angular background noise.
            return shouldLogDebouncedError(e, digestInProgress);
          }
        }
      }
    }
  }

  return true;
};

export let wrapInvocationFunction = function (oldInvocationFunction: Function, name: string, logDelays?: boolean) {
  return function () {
    if (arguments.length > 0 && arguments[0] instanceof Function) {
      let functionStack = new Error().stack;
      let startTime = new Date().getTime();
      let handler = arguments[0];
      let invocationStats = new WrappedInvocationStats();
      let lastTimeLogged = 0;
      let handlerArgs = arguments;
      let expectedDelay = parseInt(handlerArgs[1]);
      arguments[0] = function () {
        try {
          handler.apply(this, arguments);
        } catch (e) {
          logIfAble(e, true);
        }
        let now = new Date().getTime();
        let actualDelay = now - startTime;
        if (logDelays && invocationStats.isDelayed(actualDelay, expectedDelay) && now - lastTimeLogged > 60000) {
          let logObject = invocationStats.getStatsObject();
          logObject.message = `${name} did not occur when expected.`;
          logObject.expectedDelay = expectedDelay;
          logObject.arguments = arguments;
          logObject.functionStack = functionStack;
          invocationStats = new WrappedInvocationStats();
          lastTimeLogged = now;
        }
        startTime = now;
      };
    }
    return oldInvocationFunction.apply(this, arguments);
  };
};

export let wrapOnError = function (message: string, filename?: string, lineno?: number, colno?: number, error?: Error) {
  if (error) {
    if ((<any>error)._sd_processed) {
      // If we already tracked this exception in our other handlers, don't double log it.
      return;
    }
    (<ErrorDetails>error).unhandled = true;
  } else {
    error = <ErrorDetails>{
      unhandled: true,
      lineNumber: lineno,
      message: message,
      name: "Unhandled window level error"
    };
  }

  logIfAble(error, false);
};
